import React, { useState, useEffect } from 'react';
import {
    updateDoctorProfile,
    addDoctorQualification,
    updateDoctorQualification,
    deleteDoctorQualification,
    addDoctorAchievement,
    updateDoctorAchievement,
    deleteDoctorAchievement,
    addDoctorCertification,
    updateDoctorCertification,
    deleteDoctorCertification,
    uploadDoctorProfileImage
} from '../../services/api';
import Button from '../common/Button';
import BasicInfoTab from './BasicInfoTab';
import QualificationsTab from './QualificationsTab';
import AchievementsTab from './AchievementsTab';
import CertificationsTab from './CertificationsTab';
import ProfileImageTab from './ProfileImageTab';

const TABS = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'qualifications', label: 'Qualifications' },
    { key: 'achievements', label: 'Achievements' },
    { key: 'certifications', label: 'Certifications' },
    { key: 'profileImage', label: 'Profile Image' },
];

const DoctorEditModal = ({ doctor, clinicId, isOpen, onClose, onSuccess }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [basicInfo, setBasicInfo] = useState({
        specialization: '',
        experienceYears: '',
        profileImageUrl: '',
        bio: ''
    });
    const [qualifications, setQualifications] = useState([]);
    const [achievements, setAchievements] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editIndex, setEditIndex] = useState({}); // {qualifications: idx, achievements: idx, certifications: idx}
    const [editItem, setEditItem] = useState({}); // {qualifications: {...}, achievements: {...}, certifications: {...}}
    const [addItem, setAddItem] = useState({}); // {qualifications: {...}, achievements: {...}, certifications: {...}}
    const [hasChanges, setHasChanges] = useState(false); // Track if any changes were made
    const [selectedImage, setSelectedImage] = useState(null); // Track selected image file
    const [imagePreview, setImagePreview] = useState(''); // Track image preview URL

    useEffect(() => {
        if (doctor) {
            setBasicInfo({
                specialization: doctor.specialization || '',
                experienceYears: doctor.experienceYears || '',
                profileImageUrl: doctor.profileImageUrl || '',
                bio: doctor.bio || ''
            });
            setQualifications(doctor.qualifications || []);
            setAchievements(doctor.achievements || []);
            setCertifications(doctor.certifications || []);
            setHasChanges(false); // Reset changes when modal opens
        }
    }, [doctor]);

    // --- Basic Info ---
    const handleBasicInfoChange = (field, value) => {
        setBasicInfo(prev => ({ ...prev, [field]: value }));
    };
    const handleUpdateBasicInfo = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const response = await updateDoctorProfile(doctor.id, {
                doctorUserId: doctor.userId,
                clinicId,
                ...basicInfo
            });
            if (response.success) {
                setHasChanges(true); // Mark that changes were made
                setError(null);
            } else {
                setError(response.message || 'Failed to update basic info');
            }
        } catch (err) {
            setError('Failed to update basic info. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // --- Section Handlers (Generic) ---
    const handleEdit = (section, idx) => {
        setEditIndex({ ...editIndex, [section]: idx });
        setEditItem({ ...editItem, [section]: { ...getSectionList(section)[idx] } });
    };
    const handleEditChange = (section, field, value) => {
        setEditItem(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    };
    const handleCancelEdit = (section) => {
        setEditIndex({ ...editIndex, [section]: null });
        setEditItem({ ...editItem, [section]: null });
    };
    const handleAddChange = (section, field, value) => {
        setAddItem(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    };
    const handleCancelAdd = (section) => {
        setAddItem({ ...addItem, [section]: null });
    };
    const getSectionList = (section) => {
        if (section === 'qualifications') return qualifications;
        if (section === 'achievements') return achievements;
        if (section === 'certifications') return certifications;
        return [];
    };
    const setSectionList = (section, list) => {
        if (section === 'qualifications') setQualifications(list);
        if (section === 'achievements') setAchievements(list);
        if (section === 'certifications') setCertifications(list);
    };

    // --- Qualification Handlers ---
    const handleAddQualification = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await addDoctorQualification(doctor.id, addItem.qualifications);
            if (response.success) {
                setQualifications([...qualifications, response.data]);
                setAddItem({ ...addItem, qualifications: null });
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to add qualification');
            }
        } catch (err) {
            setError('Failed to add qualification.');
        } finally {
            setLoading(false);
        }
    };
    const handleUpdateQualification = async (idx) => {
        setLoading(true);
        setError(null);
        const qual = editItem.qualifications;
        try {
            const response = await updateDoctorQualification(doctor.id, qual.id, qual);
            if (response.success) {
                const updated = [...qualifications];
                updated[idx] = response.data;
                setQualifications(updated);
                handleCancelEdit('qualifications');
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to update qualification');
            }
        } catch (err) {
            setError('Failed to update qualification.');
        } finally {
            setLoading(false);
        }
    };
    const handleDeleteQualification = async (idx) => {
        setLoading(true);
        setError(null);
        const qual = qualifications[idx];
        try {
            const response = await deleteDoctorQualification(doctor.id, qual.id);
            if (response.success) {
                setQualifications(qualifications.filter((_, i) => i !== idx));
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to delete qualification');
            }
        } catch (err) {
            setError('Failed to delete qualification.');
        } finally {
            setLoading(false);
        }
    };

    // --- Achievement Handlers ---
    const handleAddAchievement = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await addDoctorAchievement(doctor.id, addItem.achievements);
            if (response.success) {
                setAchievements([...achievements, response.data]);
                setAddItem({ ...addItem, achievements: null });
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to add achievement');
            }
        } catch (err) {
            setError('Failed to add achievement.');
        } finally {
            setLoading(false);
        }
    };
    const handleUpdateAchievement = async (idx) => {
        setLoading(true);
        setError(null);
        const ach = editItem.achievements;
        try {
            const response = await updateDoctorAchievement(doctor.id, ach.id, ach);
            if (response.success) {
                const updated = [...achievements];
                updated[idx] = response.data;
                setAchievements(updated);
                handleCancelEdit('achievements');
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to update achievement');
            }
        } catch (err) {
            setError('Failed to update achievement.');
        } finally {
            setLoading(false);
        }
    };
    const handleDeleteAchievement = async (idx) => {
        setLoading(true);
        setError(null);
        const ach = achievements[idx];
        try {
            const response = await deleteDoctorAchievement(doctor.id, ach.id);
            if (response.success) {
                setAchievements(achievements.filter((_, i) => i !== idx));
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to delete achievement');
            }
        } catch (err) {
            setError('Failed to delete achievement.');
        } finally {
            setLoading(false);
        }
    };

    // --- Certification Handlers ---
    const handleAddCertification = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await addDoctorCertification(doctor.id, addItem.certifications);
            if (response.success) {
                setCertifications([...certifications, response.data]);
                setAddItem({ ...addItem, certifications: null });
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to add certification');
            }
        } catch (err) {
            setError('Failed to add certification.');
        } finally {
            setLoading(false);
        }
    };
    const handleUpdateCertification = async (idx) => {
        setLoading(true);
        setError(null);
        const cert = editItem.certifications;
        try {
            const response = await updateDoctorCertification(doctor.id, cert.id, cert);
            if (response.success) {
                const updated = [...certifications];
                updated[idx] = response.data;
                setCertifications(updated);
                handleCancelEdit('certifications');
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to update certification');
            }
        } catch (err) {
            setError('Failed to update certification.');
        } finally {
            setLoading(false);
        }
    };
    const handleDeleteCertification = async (idx) => {
        setLoading(true);
        setError(null);
        const cert = certifications[idx];
        try {
            const response = await deleteDoctorCertification(doctor.id, cert.id);
            if (response.success) {
                setCertifications(certifications.filter((_, i) => i !== idx));
                setHasChanges(true); // Mark that changes were made
            } else {
                setError(response.message || 'Failed to delete certification');
            }
        } catch (err) {
            setError('Failed to delete certification.');
        } finally {
            setLoading(false);
        }
    };

    // --- Profile Image Handlers ---
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadProfileImage = async () => {
        if (!selectedImage) {
            setError('Please select an image to upload');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await uploadDoctorProfileImage(clinicId, doctor.userId, doctor.id, selectedImage);
            if (response.success) {
                // Update the basic info with the new image URL
                setBasicInfo(prev => ({
                    ...prev,
                    profileImageUrl: response.data?.imageUrl || imagePreview
                }));
                setSelectedImage(null);
                setImagePreview('');
                setHasChanges(true);
                setError(null);
            } else {
                setError(response.message || 'Failed to upload profile image');
            }
        } catch (err) {
            setError('Failed to upload profile image. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close with refresh if changes were made
    const handleClose = () => {
        if (hasChanges && onSuccess) {
            onSuccess(); // Refresh doctor list in background
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md lg:max-w-2xl xl:max-w-4xl max-h-[90vh] flex flex-col">
                <div className="p-4 sm:p-6 border-b">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                            Edit Doctor Profile
                        </h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded-full"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="border-b overflow-x-auto">
                    <div className="flex space-x-2 sm:space-x-4 px-2 sm:px-6">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={`flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-sm sm:text-base transition-colors border-b-2 ${
                                    activeTab === tab.key
                                        ? 'border-purple-600 text-purple-600'
                                        : 'border-transparent text-gray-600 hover:text-purple-600'
                                }`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                            {error}
                        </div>
                    )}
                    {activeTab === 'basic' && (
                        <BasicInfoTab
                            basicInfo={basicInfo}
                            onBasicInfoChange={handleBasicInfoChange}
                            onUpdateBasicInfo={handleUpdateBasicInfo}
                            loading={loading}
                            error={error}
                        />
                    )}
                    {activeTab === 'qualifications' && (
                        <QualificationsTab
                            qualifications={qualifications}
                            addItem={addItem}
                            editIndex={editIndex}
                            editItem={editItem}
                            loading={loading}
                            onAddChange={handleAddChange}
                            onEditChange={handleEditChange}
                            onCancelAdd={handleCancelAdd}
                            onCancelEdit={handleCancelEdit}
                            onEdit={handleEdit}
                            onAddQualification={handleAddQualification}
                            onUpdateQualification={handleUpdateQualification}
                            onDeleteQualification={handleDeleteQualification}
                            setAddItem={setAddItem}
                        />
                    )}
                    {activeTab === 'achievements' && (
                        <AchievementsTab
                            achievements={achievements}
                            addItem={addItem}
                            editIndex={editIndex}
                            editItem={editItem}
                            loading={loading}
                            onAddChange={handleAddChange}
                            onEditChange={handleEditChange}
                            onCancelAdd={handleCancelAdd}
                            onCancelEdit={handleCancelEdit}
                            onEdit={handleEdit}
                            onAddAchievement={handleAddAchievement}
                            onUpdateAchievement={handleUpdateAchievement}
                            onDeleteAchievement={handleDeleteAchievement}
                            setAddItem={setAddItem}
                        />
                    )}
                    {activeTab === 'certifications' && (
                        <CertificationsTab
                            certifications={certifications}
                            addItem={addItem}
                            editIndex={editIndex}
                            editItem={editItem}
                            loading={loading}
                            onAddChange={handleAddChange}
                            onEditChange={handleEditChange}
                            onCancelAdd={handleCancelAdd}
                            onCancelEdit={handleCancelEdit}
                            onEdit={handleEdit}
                            onAddCertification={handleAddCertification}
                            onUpdateCertification={handleUpdateCertification}
                            onDeleteCertification={handleDeleteCertification}
                            setAddItem={setAddItem}
                        />
                    )}
                    {activeTab === 'profileImage' && (
                        <ProfileImageTab
                            basicInfo={basicInfo}
                            selectedImage={selectedImage}
                            imagePreview={imagePreview}
                            loading={loading}
                            onImageSelect={handleImageSelect}
                            onUploadProfileImage={handleUploadProfileImage}
                        />
                    )}
                </div>

                <div className="flex justify-end space-x-3 p-4 sm:p-6 border-t">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        loading={loading}
                        className="w-full sm:w-auto"
                    >
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DoctorEditModal; 